import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  CheckCircle, Circle, Buildings, Palette, Trophy, UsersThree, UsersFour,
  Student, ClipboardText, Barbell, UploadSimple, Copy, Sparkle, ArrowRight,
  SkipForward, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_META = {
  info:        { label: "Academy information", icon: Buildings },
  branding:    { label: "Logo & branding",     icon: Palette },
  sport:       { label: "Sport selection",     icon: Trophy },
  coaches:     { label: "Add coaches",         icon: UsersThree },
  teams:       { label: "Create teams",        icon: UsersFour },
  athletes:    { label: "Invite athletes",     icon: Student },
  assessments: { label: "Assessment templates", icon: ClipboardText },
  program:     { label: "First program",       icon: Barbell },
};

const BRAND_SWATCHES = ["#FF5A1F", "#22D3EE", "#A3E635", "#F43F5E", "#8B5CF6", "#FACC15", "#10B981", "#3B82F6"];

const fullLogo = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export const AcademyOnboarding = ({ academyId, academy, open, onClose, onChanged }) => {
  const [state, setState] = useState(null);
  const [active, setActive] = useState("info");

  const loadState = useCallback(async () => {
    try {
      const { data } = await api.get(`/academies/${academyId}/onboarding`);
      setState(data);
      return data;
    } catch { /* ignore */ }
  }, [academyId]);

  useEffect(() => {
    if (open) {
      loadState().then((d) => {
        if (d) {
          const first = d.steps.find((s) => !s.done);
          setActive(first ? first.key : "info");
        }
      });
    }
  }, [open, loadState]);

  const refresh = useCallback(async () => {
    await loadState();
    onChanged && onChanged();
  }, [loadState, onChanged]);

  const skipStep = async (step) => {
    try {
      const { data } = await api.post(`/academies/${academyId}/onboarding/skip-step`, { step });
      setState(data);
      const next = data.steps.find((s) => !s.done);
      if (next) setActive(next.key);
      onChanged && onChanged();
      toast.message("Step skipped — you can finish it later");
    } catch { toast.error("Could not skip step"); }
  };

  const goNext = () => {
    if (!state) return;
    const idx = state.steps.findIndex((s) => s.key === active);
    const rest = state.steps.slice(idx + 1);
    const next = rest.find((s) => !s.done) || state.steps[idx + 1];
    if (next) setActive(next.key);
  };

  const finish = async () => {
    if (state.completed) {
      try { await api.post(`/academies/${academyId}/onboarding/dismiss`); } catch { /* ignore */ }
    }
    onChanged && onChanged();
    onClose();
    if (state.completed) toast.success("Academy setup complete 🎉");
  };

  if (!state) return null;

  const stepStatus = (key) => state.steps.find((s) => s.key === key) || {};
  const commonProps = { academyId, academy, refresh, goNext, skipStep, status: stepStatus(active) };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden p-0 gap-0 bg-surface-base border border-white/10"
        data-testid="academy-onboarding-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-white/10">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-brand-accent" /> Set up your academy
          </DialogTitle>
          <DialogDescription className="sr-only">Complete the guided steps to finish setting up your academy. All steps are skippable.</DialogDescription>
          <ProgressBar done={state.done_count} total={state.total} percent={state.percent} />
        </DialogHeader>

        <div className="flex flex-col md:flex-row min-h-0 flex-1 overflow-hidden">
          {/* Stepper */}
          <nav className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-white/10 p-3 overflow-x-auto md:overflow-y-auto flex md:block gap-2" data-testid="academy-onboarding-steps">
            {state.steps.map((s, i) => {
              const Meta = STEP_META[s.key];
              const Icon = Meta.icon;
              const isActive = s.key === active;
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  data-testid={`academy-onboarding-step-${s.key}`}
                  className={`shrink-0 md:w-full flex items-center gap-2.5 px-3 py-2.5 text-left rounded transition ${
                    isActive ? "bg-brand-accent/10 text-white" : "text-text-secondary hover:bg-white/5"
                  }`}
                >
                  {s.done
                    ? <CheckCircle size={18} weight="fill" className="text-lime-400 shrink-0" data-testid={`academy-onboarding-check-${s.key}`} />
                    : <Circle size={18} className="text-white/30 shrink-0" />}
                  <Icon size={15} className={isActive ? "text-brand-accent" : "text-text-secondary"} />
                  <span className="text-xs label-tiny tracking-wide whitespace-nowrap md:whitespace-normal">
                    {i + 1}. {Meta.label}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Active step panel */}
          <div className="flex-1 min-w-0 overflow-y-auto p-6" data-testid="academy-onboarding-panel">
            {active === "info" && <InfoStep {...commonProps} />}
            {active === "branding" && <BrandingStep {...commonProps} />}
            {active === "sport" && <SportStep {...commonProps} />}
            {active === "coaches" && <InviteStep {...commonProps} kind="coaches" />}
            {active === "teams" && <TeamsStep {...commonProps} />}
            {active === "athletes" && <InviteStep {...commonProps} kind="athletes" />}
            {active === "assessments" && <AssessmentStep {...commonProps} />}
            {active === "program" && <ProgramStep {...commonProps} />}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between gap-3">
          <span className="text-xs text-text-secondary">
            {state.done_count} of {state.total} complete
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs label-tiny tracking-wider text-text-secondary hover:text-white"
              data-testid="academy-onboarding-close"
            >
              CLOSE
            </button>
            <button
              onClick={finish}
              className="bg-brand-accent text-surface-base px-5 py-2 text-xs label-tiny tracking-wider inline-flex items-center gap-2"
              data-testid="academy-onboarding-finish"
            >
              {state.completed ? "FINISH" : "SAVE & EXIT"} <X size={14} className="hidden" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProgressBar = ({ done, total, percent }) => (
  <div className="pt-2" data-testid="academy-onboarding-progress">
    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-brand-accent transition-all duration-500"
        style={{ width: `${percent}%` }}
        data-testid="academy-onboarding-progress-fill"
      />
    </div>
    <div className="mt-1.5 text-[11px] label-tiny text-text-secondary">{percent}% · {done}/{total} steps</div>
  </div>
);

const StepHeader = ({ icon: Icon, title, desc }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={20} weight="duotone" className="text-brand-accent" />
      <h3 className="font-heading text-lg">{title}</h3>
    </div>
    <p className="text-sm text-text-secondary">{desc}</p>
  </div>
);

const StepActions = ({ onSave, saving, saveLabel = "SAVE & CONTINUE", onSkip, canSkip = true, skipLabel = "SKIP FOR NOW" }) => (
  <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/10">
    {onSave && (
      <button
        onClick={onSave}
        disabled={saving}
        className="bg-brand-accent text-surface-base px-5 py-2.5 text-xs label-tiny tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="academy-onboarding-save"
      >
        {saving ? "SAVING..." : saveLabel} <ArrowRight size={14} />
      </button>
    )}
    {canSkip && onSkip && (
      <button
        onClick={onSkip}
        className="px-4 py-2.5 text-xs label-tiny tracking-wider text-text-secondary hover:text-white inline-flex items-center gap-1.5"
        data-testid="academy-onboarding-skip"
      >
        <SkipForward size={14} /> {skipLabel}
      </button>
    )}
  </div>
);

/* ---------------- Step 1: Academy information ---------------- */
const InfoStep = ({ academyId, academy, refresh, goNext, skipStep }) => {
  const [name, setName] = useState(academy.name || "");
  const [desc, setDesc] = useState(academy.description || "");
  const [web, setWeb] = useState(academy.website || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Academy name is required");
    setSaving(true);
    try {
      await api.patch(`/academies/${academyId}`, { name, description: desc, website: web });
      toast.success("Academy details saved");
      await refresh();
      goNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <StepHeader icon={Buildings} title="Academy information" desc="The basics athletes and parents will see on your public page." />
      <div className="space-y-4 max-w-xl">
        <div>
          <label className="text-xs label-tiny text-text-secondary">ACADEMY NAME</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="onboarding-info-name" />
        </div>
        <div>
          <label className="text-xs label-tiny text-text-secondary">DESCRIPTION</label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="What your academy does, who you train..." data-testid="onboarding-info-desc" />
        </div>
        <div>
          <label className="text-xs label-tiny text-text-secondary">WEBSITE (OPTIONAL)</label>
          <Input value={web} onChange={(e) => setWeb(e.target.value)} placeholder="https://..." data-testid="onboarding-info-web" />
        </div>
      </div>
      <StepActions onSave={save} saving={saving} onSkip={() => skipStep("info")} />
    </div>
  );
};

/* ---------------- Step 2: Logo & branding ---------------- */
const BrandingStep = ({ academyId, academy, refresh, goNext, skipStep }) => {
  const [logo, setLogo] = useState(academy.logo_url || "");
  const [color, setColor] = useState(academy.brand_color || "#FF5A1F");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/academies/${academyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.url);
      toast.success("Logo uploaded");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/academies/${academyId}`, { brand_color: color });
      toast.success("Branding saved");
      await refresh();
      goNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <StepHeader icon={Palette} title="Logo & branding" desc="Upload your logo and pick a brand colour used across your academy." />
      <div className="space-y-6 max-w-xl">
        <div>
          <label className="text-xs label-tiny text-text-secondary mb-2 block">LOGO</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden shrink-0" data-testid="onboarding-logo-preview">
              {logo
                ? <img src={fullLogo(logo)} alt="logo" className="w-full h-full object-contain" />
                : <Buildings size={28} className="text-white/30" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="onboarding-logo-file" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="border border-brand-accent text-brand-accent px-4 py-2 text-xs label-tiny tracking-wider inline-flex items-center gap-2 hover:bg-brand-accent/10 disabled:opacity-60"
                data-testid="onboarding-logo-upload"
              >
                <UploadSimple size={14} /> {uploading ? "UPLOADING..." : logo ? "REPLACE LOGO" : "UPLOAD LOGO"}
              </button>
              <p className="text-[11px] text-text-secondary mt-1.5">PNG, JPG, SVG or WEBP · max 5MB</p>
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs label-tiny text-text-secondary mb-2 block">BRAND COLOUR</label>
          <div className="flex items-center gap-3 flex-wrap">
            {BRAND_SWATCHES.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                data-testid={`onboarding-brand-swatch-${c.replace("#", "")}`}
                className={`w-8 h-8 rounded-full border-2 transition ${color.toLowerCase() === c.toLowerCase() ? "border-white scale-110" : "border-transparent"}`}
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
            <label className="flex items-center gap-2 ml-1">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-9 h-9 bg-transparent border border-white/10 rounded cursor-pointer" data-testid="onboarding-brand-color" />
              <span className="text-xs text-text-secondary font-mono">{color.toUpperCase()}</span>
            </label>
          </div>
        </div>
      </div>
      <StepActions onSave={save} saving={saving} onSkip={() => skipStep("branding")} />
    </div>
  );
};

/* ---------------- Step 3: Sport selection ---------------- */
const SportStep = ({ academyId, academy, refresh, goNext, skipStep }) => {
  const [sports, setSports] = useState([]);
  const [sel, setSel] = useState(academy.sport || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/academies/${academyId}/assessment-templates`).then(({ data }) => setSports(data.sports || [])).catch(() => {});
  }, [academyId]);
  const save = async () => {
    if (!sel) return toast.error("Pick a sport");
    setSaving(true);
    try {
      await api.patch(`/academies/${academyId}`, { sport: sel });
      toast.success("Sport saved");
      await refresh();
      goNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <StepHeader icon={Trophy} title="Sport selection" desc="Pick your academy's primary sport — this tailors assessment templates and programs." />
      <div className="flex flex-wrap gap-2 max-w-2xl" data-testid="onboarding-sport-list">
        {sports.map((s) => (
          <button
            key={s}
            onClick={() => setSel(s)}
            data-testid={`onboarding-sport-${s.replace(/[^a-zA-Z0-9]/g, "-")}`}
            className={`px-4 py-2 text-xs label-tiny tracking-wide border rounded-full transition ${
              sel === s ? "border-brand-accent bg-brand-accent/15 text-brand-accent" : "border-white/15 text-text-secondary hover:border-white/30"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <StepActions onSave={save} saving={saving} onSkip={() => skipStep("sport")} />
    </div>
  );
};

/* ---------------- Steps 4 & 6: Invite coaches / athletes ---------------- */
const InviteStep = ({ academyId, kind, refresh, goNext, skipStep, status }) => {
  const isCoach = kind === "coaches";
  const [role, setRole] = useState(isCoach ? "strength_coach" : "athlete");
  const [link, setLink] = useState("");
  const [creating, setCreating] = useState(false);
  const roles = isCoach
    ? [["strength_coach", "Strength Coach"], ["fitness_trainer", "Fitness Trainer"], ["sports_coach", "Sports Coach"]]
    : [["athlete", "Athlete"]];

  const createInvite = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/academies/${academyId}/invites`, { role, expires_in_days: 14 });
      const url = `${window.location.origin}/academy-join/${data.code}`;
      setLink(url);
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Invite link created & copied");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create invite"); }
    finally { setCreating(false); }
  };

  const Meta = isCoach ? STEP_META.coaches : STEP_META.athletes;
  return (
    <div>
      <StepHeader
        icon={Meta.icon}
        title={isCoach ? "Add coaches" : "Invite athletes"}
        desc={isCoach ? "Create a shareable invite link for your coaching staff." : "Create a shareable invite link for athletes to join."}
      />
      <div className="space-y-4 max-w-xl">
        {isCoach && (
          <div>
            <label className="text-xs label-tiny text-text-secondary mb-2 block">COACH ROLE</label>
            <div className="flex flex-wrap gap-2">
              {roles.map(([val, lbl]) => (
                <button
                  key={val}
                  onClick={() => setRole(val)}
                  data-testid={`onboarding-${kind}-role-${val}`}
                  className={`px-4 py-2 text-xs label-tiny tracking-wide border rounded-full transition ${
                    role === val ? "border-brand-accent bg-brand-accent/15 text-brand-accent" : "border-white/15 text-text-secondary hover:border-white/30"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={createInvite}
          disabled={creating}
          className="border border-brand-accent text-brand-accent px-5 py-2.5 text-xs label-tiny tracking-wider disabled:opacity-60"
          data-testid={`onboarding-${kind}-create-invite`}
        >
          {creating ? "CREATING..." : "CREATE INVITE LINK"}
        </button>
        {link && (
          <div className="flex items-center gap-2 border border-white/10 bg-white/5 rounded px-3 py-2.5" data-testid={`onboarding-${kind}-invite-link`}>
            <span className="text-xs text-text-secondary truncate flex-1">{link}</span>
            <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} className="text-brand-accent" data-testid={`onboarding-${kind}-copy-link`}>
              <Copy size={16} />
            </button>
          </div>
        )}
        {status?.done && (
          <p className="text-xs text-lime-400 flex items-center gap-1.5" data-testid={`onboarding-${kind}-done-note`}>
            <CheckCircle size={14} weight="fill" /> {isCoach ? "Coaches" : "Athletes"} step complete — you can invite more anytime from the Invites tab.
          </p>
        )}
      </div>
      <StepActions
        onSave={goNext}
        saving={false}
        saveLabel="CONTINUE"
        onSkip={() => skipStep(kind)}
      />
    </div>
  );
};

/* ---------------- Step 5: Create teams ---------------- */
const TeamsStep = ({ academyId, refresh, goNext, skipStep }) => {
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/academies/${academyId}/teams`).then(({ data }) => setTeams(data)).catch(() => {});
  }, [academyId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return toast.error("Team name is required");
    setSaving(true);
    try {
      await api.post(`/academies/${academyId}/teams`, { name, age_group: ageGroup });
      setName(""); setAgeGroup("");
      toast.success("Team created");
      load();
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create team"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/academies/${academyId}/teams/${id}`); load(); await refresh(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <StepHeader icon={UsersFour} title="Create teams" desc="Group your athletes into teams or squads (e.g. U14 Boys, Senior Squad)." />
      <div className="space-y-3 max-w-xl">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" data-testid="onboarding-team-name" />
          <Input value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} placeholder="Age group (optional)" className="sm:w-48" data-testid="onboarding-team-age" />
          <button onClick={add} disabled={saving} className="bg-brand-accent text-surface-base px-5 py-2.5 text-xs label-tiny tracking-wider disabled:opacity-60 shrink-0" data-testid="onboarding-team-add">
            {saving ? "..." : "ADD"}
          </button>
        </div>
        <div className="space-y-2" data-testid="onboarding-team-list">
          {teams.length === 0 && <p className="text-xs text-text-secondary">No teams yet.</p>}
          {teams.map((t) => (
            <div key={t.team_id} className="flex items-center justify-between border border-white/10 bg-white/5 rounded px-3 py-2.5" data-testid={`onboarding-team-${t.team_id}`}>
              <div>
                <span className="text-sm">{t.name}</span>
                {t.age_group && <span className="text-xs text-text-secondary ml-2">· {t.age_group}</span>}
              </div>
              <button onClick={() => remove(t.team_id)} className="text-text-secondary hover:text-heat text-xs label-tiny" data-testid={`onboarding-team-remove-${t.team_id}`}>REMOVE</button>
            </div>
          ))}
        </div>
      </div>
      <StepActions onSave={goNext} saveLabel="CONTINUE" onSkip={() => skipStep("teams")} />
    </div>
  );
};

/* ---------------- Step 7: Assessment templates ---------------- */
const AssessmentStep = ({ academyId, refresh, goNext, skipStep }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sport, setSport] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/academies/${academyId}/assessment-templates`).then(({ data }) => {
      setTemplates(data.templates || []);
      setSelected(data.selected || []);
    }).catch(() => {});
    api.get(`/academies/${academyId}`).then(({ data }) => setSport(data.sport)).catch(() => {});
  }, [academyId]);

  const toggle = (key) => setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const save = async () => {
    if (selected.length === 0) return toast.error("Pick at least one template");
    setSaving(true);
    try {
      await api.patch(`/academies/${academyId}`, { assessment_templates: selected });
      toast.success("Assessment templates saved");
      await refresh();
      goNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <StepHeader icon={ClipboardText} title="Select assessment templates" desc="Pick the group-test batteries your academy will run. Each is a single group test with one shared log sheet — not individual tests." />
      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl" data-testid="onboarding-assessment-list">
        {templates.map((t) => {
          const on = selected.includes(t.key);
          return (
            <button
              key={t.key}
              onClick={() => toggle(t.key)}
              data-testid={`onboarding-assessment-${t.key}`}
              className={`text-left p-4 rounded-lg border transition ${on ? "border-brand-accent bg-brand-accent/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-heading text-sm">{t.name}</span>
                {on ? <CheckCircle size={18} weight="fill" className="text-brand-accent shrink-0" /> : <Circle size={18} className="text-white/25 shrink-0" />}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] label-tiny px-1.5 py-0.5 rounded bg-white/10 text-text-secondary">{t.category}</span>
                {t.sport !== "general" && <span className="text-[10px] label-tiny px-1.5 py-0.5 rounded bg-brand-accent/15 text-brand-accent">{t.sport}</span>}
              </div>
              <p className="text-xs text-text-secondary mt-2">{t.description}</p>
              <p className="text-[11px] text-text-secondary mt-1.5">{t.items.length} tests · {t.items.map((i) => i.name).join(", ")}</p>
            </button>
          );
        })}
      </div>
      {!sport && <p className="text-[11px] text-amber-300 mt-3">Tip: set your sport first to also see sport-specific batteries.</p>}
      <StepActions onSave={save} saving={saving} onSkip={() => skipStep("assessments")} />
    </div>
  );
};

/* ---------------- Step 8: First program ---------------- */
const ProgramStep = ({ academyId, academy, refresh, goNext, skipStep }) => {
  const [mode, setMode] = useState("library");
  const [library, setLibrary] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState("");
  const [days, setDays] = useState(3);
  const [weeks, setWeeks] = useState(4);

  const load = useCallback(() => {
    api.get(`/academies/${academyId}/programs`).then(({ data }) => {
      setLibrary(data.library || []);
      setPrograms(data.programs || []);
    }).catch(() => {});
  }, [academyId]);
  useEffect(() => { load(); }, [load]);

  const pickLibrary = async (key) => {
    setBusy(true);
    try {
      await api.post(`/academies/${academyId}/programs/from-library`, { key });
      toast.success("Program added");
      load(); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/academies/${academyId}/programs/generate`, { goal, days_per_week: days, weeks });
      toast.success("AI program created");
      load(); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <StepHeader icon={Barbell} title="Create your first program" desc="Generate one with AI, pick a ready-made starter, or build it later." />
      <div className="flex gap-2 mb-5">
        {[["library", "Pick from library"], ["ai", "Generate with AI"]].map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => setMode(val)}
            data-testid={`onboarding-program-mode-${val}`}
            className={`px-4 py-2 text-xs label-tiny tracking-wide border rounded-full transition ${mode === val ? "border-brand-accent bg-brand-accent/15 text-brand-accent" : "border-white/15 text-text-secondary hover:border-white/30"}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {programs.length > 0 && (
        <div className="mb-5 p-3 rounded-lg border border-lime-400/30 bg-lime-400/5" data-testid="onboarding-program-created">
          <p className="text-xs text-lime-400 flex items-center gap-1.5"><CheckCircle size={14} weight="fill" /> {programs.length} program{programs.length > 1 ? "s" : ""} created</p>
          {programs.slice(0, 3).map((p) => <p key={p.program_id} className="text-xs text-text-secondary mt-1">· {p.name}</p>)}
        </div>
      )}

      {mode === "library" && (
        <div className="grid sm:grid-cols-2 gap-3 max-w-3xl" data-testid="onboarding-program-library">
          {library.map((p) => (
            <div key={p.key} className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`onboarding-program-lib-${p.key}`}>
              <div className="font-heading text-sm">{p.name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] label-tiny px-1.5 py-0.5 rounded bg-white/10 text-text-secondary">{p.weeks}wk · {p.days_per_week}d/wk</span>
              </div>
              <p className="text-xs text-text-secondary mt-2">{p.summary}</p>
              <button onClick={() => pickLibrary(p.key)} disabled={busy} className="mt-3 border border-brand-accent text-brand-accent px-4 py-1.5 text-xs label-tiny tracking-wider disabled:opacity-60" data-testid={`onboarding-program-use-${p.key}`}>
                USE THIS
              </button>
            </div>
          ))}
        </div>
      )}

      {mode === "ai" && (
        <div className="space-y-4 max-w-xl">
          <div>
            <label className="text-xs label-tiny text-text-secondary">GOAL</label>
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={`e.g. pre-season ${academy.sport || "athletic"} conditioning`} data-testid="onboarding-program-goal" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs label-tiny text-text-secondary">DAYS / WEEK</label>
              <Input type="number" min={1} max={6} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="onboarding-program-days" />
            </div>
            <div className="flex-1">
              <label className="text-xs label-tiny text-text-secondary">WEEKS</label>
              <Input type="number" min={1} max={12} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="onboarding-program-weeks" />
            </div>
          </div>
          <button onClick={generate} disabled={busy} className="bg-brand-accent text-surface-base px-5 py-2.5 text-xs label-tiny tracking-wider inline-flex items-center gap-2 disabled:opacity-60" data-testid="onboarding-program-generate">
            <Sparkle size={14} weight="fill" /> {busy ? "GENERATING..." : "GENERATE WITH AI"}
          </button>
        </div>
      )}

      <StepActions onSave={goNext} saveLabel="CONTINUE" onSkip={() => skipStep("program")} skipLabel="BUILD LATER" />
    </div>
  );
};
